import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Shield } from 'lucide-react'
import type { Role } from '@/types/entities'

interface RoleDeleteDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    role: Role | null
    onConfirm: () => void
    isLoading?: boolean
}

export function RoleDeleteDialog({
    open,
    onOpenChange,
    role,
    onConfirm,
    isLoading = false,
}: RoleDeleteDialogProps) {
    const { t } = useTranslation()
    if (!role) return null

    // Check if role is a system role
    const isSystemRole = role.hasOwnProperty('isSystem')
        ? (role as any).isSystem
        : role.name === 'admin' || role.name === 'user' || role.name === 'viewer'

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-destructive" />
                        {t('roles.deleteRoleTitle')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('roles.confirmDelete', { name: role.name })}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* System Role Warning */}
                    {isSystemRole && (
                        <Alert variant="destructive">
                            <Shield className="h-4 w-4" />
                            <AlertDescription>
                                {t('roles.cannotEditSystemRoleDesc')}
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* General Warning */}
                    <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            {t('roles.deleteWarning')}
                        </AlertDescription>
                    </Alert>

                    {/* Role Details */}
                    <div className="bg-muted p-3 rounded-lg">
                        <div className="text-sm space-y-1">
                            <div><strong>{t('roles.name')}:</strong> {role.name}</div>
                            <div><strong>{t('roles.description')}:</strong> {role.description || t('roles.noDescription')}</div>
                            <div><strong>{t('roles.permissions')}:</strong> {role.permissions?.length || 0}</div>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading || isSystemRole}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={isLoading || isSystemRole}
                    >
                        {isLoading ? t('common.deleting') : t('roles.delete')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

